import { NA_VALUE, PercentageFormatter, REVENUE, SESSION } from './consts';

export const safeDivide = (numerator, denominator) => {
    return denominator && denominator !== 0 ? numerator / denominator : 0;
};

export const calculateUplift = (testValue, controlValue) => {
    return controlValue && controlValue !== 0 ? (testValue - controlValue) / controlValue : '';
};

export const mergeArrays = (arr1, arr2) => [...new Set([...arr1, ...arr2])];

export const safeFormat = (formatter, value, fallback = '0') => {
    try {
        return formatter.format(value || 0);
    } catch {
        return fallback;
    }
};

const calcERF = x => {
    // Abramowitz and Stegun formula 7.1.26
    const sign = Math.sign(x);
    x = Math.abs(x);

    const a1 = 0.254829592;
    const a2 = -0.284496736;
    const a3 = 1.421413741;
    const a4 = -1.453152027;
    const a5 = 1.061405429;
    const p = 0.3275911;

    const t = 1 / (1 + p * x);
    const y = 1 - ((((a5 * t + a4) * t + a3) * t + a2) * t + a1) * t * Math.exp(-x * x);

    return sign * y;
};

const normalCDF = x => {
    return 0.5 * (1 + calcERF(x / Math.sqrt(2)));
};

// Calculate p-value using appropriate statistical test based on metric type
export const calculatePValue = (testMetric, controlMetric, testGroupSize, controlGroupSize, metricType = 'auto') => {
    // Check for valid inputs
    if (!testGroupSize || !controlGroupSize || testGroupSize <= 0 || controlGroupSize <= 0) {
        return null;
    }

    const testRate = safeDivide(testMetric, testGroupSize);
    const controlRate = safeDivide(controlMetric, controlGroupSize);

    // Determine test type
    let isProportionTest;
    if (metricType === 'conversion') {
        isProportionTest = true;
    } else if (metricType === 'count' || metricType === 'revenue') {
        isProportionTest = false;
    } else {
        // Auto-detect: if metrics are much smaller than group sizes, likely conversion rates
        // Use a more conservative threshold to catch edge cases
        const maxRate = Math.max(testRate, controlRate);
        isProportionTest = maxRate <= 0.5 && testMetric <= testGroupSize && controlMetric <= controlGroupSize;
    }

    if (isProportionTest) {
        // Two-proportion z-test for conversion rates
        const pCombined = safeDivide(testMetric + controlMetric, testGroupSize + controlGroupSize);
        const standardError = Math.sqrt(pCombined * (1 - pCombined) * (1 / testGroupSize + 1 / controlGroupSize));
        const zScore = standardError === 0 ? 0 : (testRate - controlRate) / standardError;
        return 2 * (1 - normalCDF(Math.abs(zScore)));
    } else {
        // Two-sample t-test approximation for rates/means (e.g., events per user, revenue per user)
        // Assume normal distribution for the sample means

        // Estimate variance assuming Poisson-like distribution for count data
        const testVariance = safeDivide(testRate, testGroupSize); // Variance of sample mean
        const controlVariance = safeDivide(controlRate, controlGroupSize);

        // Pooled standard error for difference in means
        const pooledStandardError = Math.sqrt(testVariance + controlVariance);

        if (pooledStandardError === 0) return null;

        // Z-score for difference in means
        const zScore = (testRate - controlRate) / pooledStandardError;

        // Two-tailed p-value
        return 2 * (1 - normalCDF(Math.abs(zScore)));
    }
};

export const calculateUpliftValue = (testMetric, controlMetric, testGroupSize, controlGroupSize) => {
    const testRate = safeDivide(testMetric, testGroupSize);
    const controlRate = safeDivide(controlMetric, controlGroupSize);

    return calculateUplift(testRate, controlRate);
};

export const calculateUpliftWithSignificance = (
    testMetric,
    controlMetric,
    testGroupSize,
    controlGroupSize,
    metricType = 'auto'
) => {
    const uplift = calculateUpliftValue(testMetric, controlMetric, testGroupSize, controlGroupSize);
    const pValue = calculatePValue(testMetric, controlMetric, testGroupSize, controlGroupSize, metricType);
    const isSignificant = pValue !== null && pValue < 0.05;

    return {
        uplift,
        isSignificant,
    };
};

export const getIncrementality = (channel, channelMetrics, controlMetrics, eventName, metricValue, translate) => {
    if (channel.isControlGroup) {
        return translate(
            'STATIC.PAGES.AUDIENCES.AUDIENCE_AB_TESTING.REPORTING.TABLE_DATA.UNIQUE_INCREMENTALITY.CONTROL_GROUP_MESSAGE'
        );
    }

    if (metricValue === 'revenue' && eventName === SESSION) return NA_VALUE;

    const uplift = calculateUplift(
        safeDivide(channelMetrics[metricValue], channelMetrics.groupSize),
        safeDivide(controlMetrics[metricValue], controlMetrics.groupSize)
    );

    return uplift === '' ? NA_VALUE : safeFormat(PercentageFormatter, uplift);
};

export const formatDisplayDate = dateString => {
    return new Intl.DateTimeFormat('en-US', {
        month: 'short',
        day: 'numeric',
    }).format(new Date(dateString));
};

const TRANSLATION_PREFIX = 'STATIC.PAGES.AUDIENCES.AUDIENCE_AB_TESTING.REPORTING.TABLE_DATA';

const formatEventName = eventName => {
    if (eventName === SESSION) return 'Session';
    if (eventName === REVENUE) return 'Revenue';
    return eventName;
};

export const getMetricLabels = (translate, eventName) => {
    const formattedEventName = formatEventName(eventName);
    return {
        GROUP_SIZE: translate(`${TRANSLATION_PREFIX}.GROUP_SIZE.LABEL`),
        EVENT_COUNT: translate(`${TRANSLATION_PREFIX}.EVENT_COUNT.LABEL`, { eventName: formattedEventName }),
        ENGAGEMENT_RATE: translate(`${TRANSLATION_PREFIX}.ENGAGEMENT_RATE.LABEL`, {
            eventName: formattedEventName,
        }),
        ENGAGEMENT_INCREMENTALITY: translate(`${TRANSLATION_PREFIX}.ENGAGEMENT_INCREMENTALITY.LABEL`, {
            eventName: formattedEventName,
        }),
        UNIQUE_EVENT_COUNT: translate(`${TRANSLATION_PREFIX}.UNIQUE_EVENT_COUNT.LABEL`, {
            eventName: formattedEventName,
        }),
        UNIQUE_CONVERSION_RATE: translate(`${TRANSLATION_PREFIX}.UNIQUE_CONVERSION_RATE.LABEL`, {
            eventName: formattedEventName,
        }),
        UNIQUE_INCREMENTALITY: translate(`${TRANSLATION_PREFIX}.UNIQUE_INCREMENTALITY.LABEL`, {
            eventName: formattedEventName,
        }),
        INCREMENTALITY_REVENUE: translate(`${TRANSLATION_PREFIX}.INCREMENTALITY_REVENUE.LABEL`),
        TOTAL_REVENUE: translate(`${TRANSLATION_PREFIX}.TOTAL_REVENUE.LABEL`),
    };
};

export const getMetricTooltips = translate => ({
    GROUP_SIZE: translate(`${TRANSLATION_PREFIX}.GROUP_SIZE.TOOLTIP`),
    EVENT_COUNT: translate(`${TRANSLATION_PREFIX}.EVENT_COUNT.TOOLTIP`),
    ENGAGEMENT_RATE: translate(`${TRANSLATION_PREFIX}.ENGAGEMENT_RATE.TOOLTIP`),
    ENGAGEMENT_INCREMENTALITY: translate(`${TRANSLATION_PREFIX}.ENGAGEMENT_INCREMENTALITY.TOOLTIP`),
    UNIQUE_EVENT_COUNT: translate(`${TRANSLATION_PREFIX}.UNIQUE_EVENT_COUNT.TOOLTIP`),
    UNIQUE_CONVERSION_RATE: translate(`${TRANSLATION_PREFIX}.UNIQUE_CONVERSION_RATE.TOOLTIP`),
    UNIQUE_INCREMENTALITY: translate(`${TRANSLATION_PREFIX}.UNIQUE_INCREMENTALITY.TOOLTIP`),
    INCREMENTALITY_REVENUE: translate(`${TRANSLATION_PREFIX}.INCREMENTALITY_REVENUE.TOOLTIP`),
    TOTAL_REVENUE: translate(`${TRANSLATION_PREFIX}.TOTAL_REVENUE.TOOLTIP`),
});
